import { defaultMonthlyPensionPayment, FormOfPaymentOptions } from 'app/calcs/pensions'
import { IMonthlyPensionPayments, SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces'
import { ISpousalPensionInputs, SpousalPensionCalculations } from 'app/calcs/pensions/spousal'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import MaterialNumber from 'app/components/material/MaterialNumber';
import { FormOfPayment } from 'app/components/questions/FormOfPayment'
import { IProfileContainerState, PensionQuestionsGroupContainer } from 'app/containers/PipelineSection/Pension/Container'
import { IQuestionsGroupContainerProps } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { IRetirementPerson, isValidPerson } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import { getBrandHelpers } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Button, Col, FormGroup, Row } from 'react-bootstrap'

interface ISpousePensionContainerState extends IProfileContainerState {
  spouse: IRetirementPerson
  monthlyPayments: IMonthlyPensionPayments
  monthlySalary: number
  ageSalaryStops: moment.Duration
  pensionAmount: number
  formOfPayment: SimplePensionFormOfPayment
  beginPaymentsAge: moment.Duration
}

const defaultBeginPaymentsAge = moment.duration(55, 'years')

class SpousePensionContainer extends PensionQuestionsGroupContainer<IQuestionsGroupContainerProps, ISpousePensionContainerState> {

  state: ISpousePensionContainerState = {
    profile: null,
    spouse: null,
    monthlyPayments: defaultMonthlyPensionPayment,
    monthlySalary: null,
    ageSalaryStops: null,
    pensionAmount: 0,
    formOfPayment: SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor,
    beginPaymentsAge: null
  }

  initCalc = false

  debounceCalculations = _.debounce((spousal: ISpousalPensionInputs) => {
    const computer = new SpousalPensionCalculations(this.state.profile)
    const mp = computer.getMonthlyPensionAmount(this.state.spouse, spousal, utils.todaysDate())
      .then((monthlyPayments) => {
        this.setState({ monthlyPayments })
      })
  }, 300)

  private spousalMonthlySalaryNS = 'pension.spousal.monthlySalary'
  private spousalAgeSalaryStopsNS = 'pension.spousal.ageSalaryStops'
  private spousalPensionAmountNS = 'pension.spousal.pensionAmount'
  private spousalKindNS = 'pension.spousal.kind'
  private spousalBeginPaymentsAgeNS = 'pension.spousal.beginPaymentsAge'

  componentWillMount() {
    this.state.monthlySalary = this.getValueForQuestion(this.spousalMonthlySalaryNS) || 0
    this.state.ageSalaryStops = this.getValueForQuestion(this.spousalAgeSalaryStopsNS) || defaultBeginPaymentsAge
    this.state.pensionAmount = this.getValueForQuestion(this.spousalPensionAmountNS) || 0
    this.state.formOfPayment = this.getValueForQuestion(this.spousalKindNS) || SimplePensionFormOfPayment.singleLifeOnly
    this.state.beginPaymentsAge = this.getValueForQuestion(this.spousalBeginPaymentsAgeNS) || defaultBeginPaymentsAge

    this.onAppStateUpdate((appState) => {
      const profile = appState.Profile.getNative('info') || null
      const spouse = profile.getSpouse()

      if (isValidPerson(spouse) && profile.pension.spousal) {
          this.debounceCalculations(profile.pension.spousal)
      }

      this.setState({ profile, spouse })
    })
  }

  componentWillUpdate(nextProps: IQuestionsGroupContainerProps, nextState: ISpousePensionContainerState) {
    this.updateCalculations(nextState)
  }

  updateCalculations(nextState: ISpousePensionContainerState) {
    const { profile, spouse } = nextState

    if (!profile) { return }

    profile.pension = profile.pension || {}
    nextState.ageSalaryStops = nextState.ageSalaryStops || moment.duration(65, 'years')
    nextState.beginPaymentsAge = nextState.beginPaymentsAge || moment.duration(spouse.selectedRetirementAge, 'years')

    const spousalPension = {
      monthlySalary: nextState.monthlySalary,
      ageSalaryStops: nextState.ageSalaryStops,
      pensionAmount: nextState.pensionAmount,
      kind: nextState.formOfPayment,
      beginPaymentsAge: nextState.beginPaymentsAge
    }

    if (!this.initCalc || !_.isEqual(spousalPension, profile.pension.spousal)) {
      this.initCalc = true

      profile.pension.spousal = spousalPension
      // tslint:disable-next-line:no-console
      console.log('spousal- updating profile in state')
      this.setState({ profile })
      this.debounceCalculations(profile.pension.spousal)
    }
  }

  updateAgeSalaryStops(ageSalaryStops: moment.Duration) {
    this.state.profile.pension.spousal.ageSalaryStops = ageSalaryStops
    this.setState({ ageSalaryStops })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  updateBeginPaymentsAgeAge(beginPaymentsAge: moment.Duration) {
    this.state.profile.pension.spousal.beginPaymentsAge = beginPaymentsAge
    this.setState({ beginPaymentsAge })

    this.markSignificantProfileUpdate(this.state.profile)
  }

  renderNoSpouse() {
    return (
      <section className="animated fadeIn">
        <h1>Oops, not so fast!</h1>

        <Row>
          <h5 className="lead">You don't have a spouse defined in your profile.</h5>
            <Button bsStyle="primary" bsSize="lg"
              onClick={() => this.relocate('#/profile', { redirectTo: '#/pension/spouse_pension', btnText: 'Get Started on Pension' })}>
              Update Profile
            </Button>
        </Row>
      </section>
    )
  }

  render() {
    if (!this.state.profile.spouseIsValid()) { return this.renderNoSpouse() }
    if (!this.state.profile.pension || !this.state.profile.pension.spousal || !this.state.beginPaymentsAge) { return null }

    const currentFormOfPayment = FormOfPaymentOptions
      .find((fopo) => fopo.value === this.state.formOfPayment)
      .label

    const spousePossessive = utils.personPossessive(this.state.spouse.firstName)
    const monthlyPaymentAmountText = this.state.monthlyPayments.amount ? utils.toCurrency(this.state.monthlyPayments.amount) : 'n/a'
    const beginPaymentsAgeText = utils.toNiceAge(this.state.monthlyPayments.beginPaymentsAge)
    const showSummary = !!this.state.monthlyPayments.amount

    const salaryQuestion = 'What is ' + spousePossessive + ' monthly salary?'
    const pensionQuestion = 'What is ' + spousePossessive + ' expected monthly pension?'

    return (
      <section className="animated fadeIn">
        <h1>{spousePossessive} Pension &amp; Income</h1>
        <h5 className="lead">Please input information about any pension your spouse may have earned.</h5>

        <Row>
          <Col md={6}>
            {/*<h5 className="formGroupTitle">Current Salary Information</h5>*/}
            <FormGroup>
              <MaterialNumber placeholder={salaryQuestion}
                formattedNumberMode
                onChange={(e) => this.setStateAndSave({ monthlySalary: this.toNumber(e.currentTarget.value) })}
                name={this.spousalMonthlySalaryNS}
                value={this.state.monthlySalary}
              />
            </FormGroup>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age will this salary stop?"
                selectedValue={this.state.ageSalaryStops} onChange={(a) => this.updateAgeSalaryStops(a)} />
            </FormGroup>

            {/*<h5 className="formGroupTitle">Existing Pension Information</h5>*/}
            <FormGroup>
              <MaterialNumber placeholder={pensionQuestion}
                formattedNumberMode
                onChange={(e) => this.setStateAndSave({ pensionAmount: this.toNumber(e.currentTarget.value) })}
                name={this.spousalPensionAmountNS}
                value={this.state.pensionAmount}
              />
            </FormGroup>

            <FormGroup>
              <RetirementAgeSelector min={55} max={70}
                placeholder="What age do you want to begin payments?"
                selectedValue={this.state.beginPaymentsAge} onChange={(a) => this.updateBeginPaymentsAgeAge(a)} />
            </FormGroup>

            <FormGroup>
              <FormOfPayment
                name={this.spousalKindNS}
                defaultValue={this.state.formOfPayment}
                options={getBrandHelpers().spousalPensionFormsOfPayment}
                onChange={(formOfPayment) => this.setStateAndSave({ formOfPayment })} />
            </FormGroup>
          </Col>
          <Col md={6}>
            {showSummary && <h2 className="animated fadeIn">
              If {this.state.spouse.firstName} begins to take payments at age&nbsp;
              <a>{beginPaymentsAgeText}</a>&nbsp;
              in the form of&nbsp;
              <a>{currentFormOfPayment}</a>&nbsp;
              the monthly amount will be <a>{monthlyPaymentAmountText}</a>.&nbsp;
            </h2>}

          </Col>
        </Row>
      </section>
    )
  }
}

const SpousePensionQuestionsGroup = () => (
  <SpousePensionContainer
    header="Spouse Pension" icon="" />
)

export {
  SpousePensionQuestionsGroup,
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Pension/Spouse Pension/index.tsx